import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@common/button';
import Text from '@common/text';
import { BirthDate, Name } from '@common/inputFields';
import Mobile from '@common/inputFields/Mobile';
import { useToast } from '@common/toast';
import Provider from '@components/profile/modals/nhisCareerModal/step2/Provider';
import { useNHISAuthRequest } from '@repository/nhis/useNHIS';
import nhisCareerSchema from '@components/profile/modals/nhisCareerModal/step2/nhisCareerSchema';
import { useBasicInfo } from '@repository/users/useUsers';
import NhisRequestAuthRq from '@domain/rq/nhis/NhisRequestAuthRq';
import { FontStyle } from '@domain/constant/fontStyle';
import { NhisProvider, NhisProviderType } from '@domain/constant/nhis/NHISProviderType';
import { getAtLeast15YearsOldDate } from '@utils/validators/validators';
import classnames from 'classnames/bind';
import styles from './index.module.scss';

const cx = classnames.bind(styles);

interface Step2Props {
  prevStep: () => void;
  nextStep: () => void;
  setProvider: (provider: NhisProviderType | null) => void;
}

const Step2 = ({ prevStep, nextStep, setProvider }: Step2Props) => {
  const { data: userData } = useBasicInfo();
  const methods = useForm<NhisRequestAuthRq>({
    mode: 'onChange',
    defaultValues: {
      provider: NhisProvider.KAKAO,
      name: undefined,
      birthDay: undefined,
      phone: undefined,
    },
    resolver: yupResolver(nhisCareerSchema) as any,
  });
  const { handleSubmit, setError, reset } = methods;
  const { mutateAsync } = useNHISAuthRequest();
  const Toast = useToast();

  useEffect(() => {
    if (!userData) return;
    reset({
      provider: NhisProvider.KAKAO,
      name: userData?.name,
      birthDay: userData?.birthDate,
      phone: userData?.mobile?.toString(),
    });
  }, [userData]);

  const onSubmit = async (value: NhisRequestAuthRq) => {
    await mutateAsync({
      ...value,
      birthDay: value.birthDay?.replaceAll('-', ''),
    })
      .then(() => {
        nextStep();
        setProvider(value.provider);
      })
      .catch((e) => {
        Toast({ type: 'error', iconType: 'info', content: '요청에 실패했어요. 인증 정보 및 인증서를 확인해주세요.' });
      });
  };

  return (
    <FormProvider {...methods}>
      <form className={cx('area')} onSubmit={handleSubmit(onSubmit)}>
        <div className={cx('wrap')}>
          <em className={cx('desc')}>간편인증을 위해 다음 정보를 입력해주세요.</em>

          <div className={cx('authArea')}>
            <Text fontStyle={FontStyle.B2_B}>인증방식</Text>
            <Provider name={'provider'} />
          </div>

          <div className={cx('inputWrap')}>
            <Text fontStyle={FontStyle.B2_B}>이름</Text>
            <Name name={'name'} />
          </div>

          <div className={cx('inputWrap')}>
            <Text fontStyle={FontStyle.B2_B}>생년월일</Text>
            <BirthDate
              name={'birthDay'}
              maxDate={getAtLeast15YearsOldDate()}
              placeholderText={'생년월일을 입력해주세요.'}
            />
          </div>

          <div className={cx('inputWrap')}>
            <Text fontStyle={FontStyle.B2_B}>휴대폰 번호</Text>
            <Mobile name={'phone'} />
          </div>
        </div>

        <div className={cx('btnArea')}>
          <button className={cx('prevBtn')} onClick={prevStep}>
            이전
          </button>
          <Button className={cx('btn')} type={'submit'} disabled={false}>
            인증 요청하기
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default Step2;
